import { useEffect, useState, useCallback, useRef } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { CollapsibleCard } from "@/components/CollapsibleCard";
import { StickySaveBar } from "@/components/StickySaveBar";
import {
  Globe, Copy, ExternalLink, ImagePlus, Save, UserPlus, Check, X, Phone,
  CreditCard, Link2, Building2, FileText, Plus, Trash2, Palette, Search, FolderOpen, MessageSquareText,
} from "lucide-react";
import { V } from "@/lib/vocab";

// Accent presets for the public page — the community picks its own look.
const BRAND_PRESETS = [
  ["#D32F2F", "Redline red"], ["#1B7F4B", "Forest green"], ["#1D5FBF", "Lake blue"],
  ["#0D9488", "Teal"], ["#7C3AED", "Violet"], ["#B45309", "Amber"], ["#BE185D", "Berry"],
];

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-sm font-semibold">{label}{hint && <span className="font-normal text-muted-foreground"> — {hint}</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
const inputCls = "w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";
const fmtStamp = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

export default function PublicPage() {
  const { tenantId, role } = useDash();
  const readOnly = role === "board_member";
  const [site, setSite] = useCachedState(`pubsite:${tenantId}`, null);
  const [conciergeNum, setConciergeNum] = useCachedState(`pubsite-num:${tenantId}`, "");
  const [docs, setDocs] = useCachedState(`pubsite-docs:${tenantId}`, []);
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useCachedState(`pubsite-reqs:${tenantId}`, []);
  const [optinLink, setOptinLink] = useCachedState(`pubsite-optin:${tenantId}`, null);
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);
  const [docSearch, setDocSearch] = useState("");
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  // Cache renders the editor instantly — a background refresh must never clobber edits.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Unsaved-changes guard: warn before the tab closes with edits that weren't saved.
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const load = useCallback(() => {
    api.get(`/tenants/${tenantId}/public-site`).then((r) => { if (!dirtyRef.current) setSite(r.data.site); setConciergeNum(r.data.concierge_number); setDocs(r.data.documents || []); }).catch(() => toast.error("Couldn't load page settings"));
    api.get(`/tenants/${tenantId}/access-requests`).then((r) => setRequests(r.data.items || [])).catch(() => {});
    api.get(`/tenants/${tenantId}/optin-link`).then((r) => setOptinLink(r.data)).catch(() => setOptinLink(null));
  }, [tenantId]);
  useEffect(() => { if (tenantId) load(); }, [tenantId, load]);

  const upd = (k, v) => { setSite((s) => ({ ...s, [k]: v })); setDirty(true); };
  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      const r = await api.put(`/tenants/${tenantId}/public-site`, { ...site, ...overrides });
      setSite(r.data.site);
      setDirty(false);
      toast.success(r.data.site.enabled ? `Saved — your ${V.lc("org")} page is live 🎉` : "Saved as draft");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Keep the photo under 8 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("photo_path", r.data.path);
      toast.success("Photo uploaded — hit Save to publish it");
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Keep the logo under 4 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("logo_path", r.data.path);
      toast.success("Logo uploaded — hit Save to publish it");
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const decide = async (req, action) => {
    setBusy(req.id + action);
    try {
      const r = await api.post(`/tenants/${tenantId}/access-requests/${req.id}/${action}`);
      if (action === "approve") {
        const sms = r.data?.texted ? " · welcome text sent, they can log in now"
          : ` · NOT texted (${r.data?.text_note || "unknown reason"}) — they can still log in`;
        r.data?.texted ? toast.success(`${req.name} approved${sms}`) : toast.warning(`${req.name} approved${sms}`);
      } else {
        toast.success("Request denied");
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't do that"); }
    finally { setBusy(""); }
  };

  if (!site) return <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>;

  const liveUrl = `${window.location.origin}/c/${site.slug}`;
  const pending = requests.filter((r) => r.status === "pending");
  const decided = requests.filter((r) => r.status !== "pending").slice(0, 10);

  return (
    <div className="space-y-6" data-testid="public-page-admin">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <PageTitle title={<><Globe className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Public Page</>} about={<>Your {V.lc("org")}'s public website — front door for {V.lcs("person")}, neighbors and prospective buyers. The concierge number takes center stage.</>} />
        </div>
        {!readOnly && (
          <button data-testid="site-save-btn" disabled={saving} onClick={() => save()}
            className={`rounded-full font-semibold px-5 py-2.5 flex items-center gap-2 transition-colors disabled:opacity-60 ${dirty ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-redline-500 text-white hover:bg-redline-600"}`}>
            <Save className="h-4 w-4" /> {saving ? "Saving…" : dirty ? "Unsaved changes — Save" : "Save"}</button>
        )}
      </div>

      {/* status bar */}
      <div className="bg-white rounded-2xl border border-sand-200 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="site-status-bar">
        <div className="flex items-center gap-3">
          <Switch data-testid="site-enabled-toggle" disabled={readOnly} checked={!!site.enabled} onCheckedChange={(v) => { upd("enabled", v); save({ enabled: v }); }} />
          <div>
            <div className="font-semibold text-sm">{site.enabled ? "Page is live" : "Page is offline (draft)"}</div>
            <div className="text-xs text-muted-foreground">{site.enabled ? "Anyone with the link can see it." : "Flip the switch when you're ready to publish."}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <code className="text-xs bg-sand-100 rounded-lg px-3 py-2 truncate max-w-[280px]" data-testid="site-url">{liveUrl}</code>
          <button data-testid="site-copy-url" onClick={() => { navigator.clipboard.writeText(liveUrl); toast.success("Link copied"); }} className="h-9 w-9 rounded-lg hover:bg-sand-200 text-muted-foreground hover:text-ink flex items-center justify-center transition-colors"><Copy className="h-4 w-4" /></button>
          <a data-testid="site-preview-link" href={`/c/${site.slug}`} target="_blank" rel="noreferrer" className="h-9 rounded-lg px-3 hover:bg-sand-200 text-muted-foreground hover:text-ink flex items-center gap-1.5 text-xs font-bold transition-colors"><ExternalLink className="h-4 w-4" /> Preview</a>
        </div>
      </div>

      {/* SMS opt-in link — the URL managers put on signage / welcome packets / QR codes.
          Branded via Short.io when a `docs_domain` is set, otherwise the platform link. */}
      <div className="bg-white rounded-2xl border border-sand-200 p-5" data-testid="optin-link-panel">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <h2 className="font-heading font-bold text-ink flex items-center gap-2"><MessageSquareText className="h-4.5 w-4.5 text-redline-500" /> SMS opt-in link</h2>
            <p className="text-xs text-muted-foreground mt-0.5">The {V.lc("org")}-branded page {V.lcs("person")} visit to opt into your texts. Share on signage, welcome packets, and QR codes.</p>
          </div>
        </div>
        {optinLink?.short_url ? (
          <>
            <div className="flex items-center gap-2 min-w-0 mb-2">
              <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 shrink-0">Branded ·</div>
              <code data-testid="optin-short-url" className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 truncate flex-1">{optinLink.short_url}</code>
              <button data-testid="optin-copy-short" onClick={() => { navigator.clipboard.writeText(optinLink.short_url); toast.success("Short link copied"); }} className="h-9 w-9 rounded-lg hover:bg-sand-200 text-muted-foreground hover:text-ink flex items-center justify-center transition-colors"><Copy className="h-4 w-4" /></button>
              <a href={optinLink.short_url} target="_blank" rel="noreferrer" className="h-9 rounded-lg px-3 hover:bg-sand-200 text-muted-foreground hover:text-ink flex items-center gap-1.5 text-xs font-bold transition-colors"><ExternalLink className="h-4 w-4" /> Open</a>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground shrink-0">Long ·</div>
              <code data-testid="optin-long-url" className="text-xs bg-sand-100 rounded-lg px-3 py-2 truncate flex-1">{optinLink.long_url}</code>
              <button data-testid="optin-copy-long" onClick={() => { navigator.clipboard.writeText(optinLink.long_url); toast.success("Link copied"); }} className="h-9 w-9 rounded-lg hover:bg-sand-200 text-muted-foreground hover:text-ink flex items-center justify-center transition-colors"><Copy className="h-4 w-4" /></button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 min-w-0 mb-2">
              <code data-testid="optin-long-url" className="text-xs bg-sand-100 rounded-lg px-3 py-2 truncate flex-1">{optinLink?.long_url || "…"}</code>
              <button data-testid="optin-copy-long" disabled={!optinLink?.long_url} onClick={() => { navigator.clipboard.writeText(optinLink.long_url); toast.success("Link copied"); }} className="h-9 w-9 rounded-lg hover:bg-sand-200 text-muted-foreground hover:text-ink flex items-center justify-center transition-colors disabled:opacity-50"><Copy className="h-4 w-4" /></button>
              <a href={optinLink?.long_url || "#"} target="_blank" rel="noreferrer" className="h-9 rounded-lg px-3 hover:bg-sand-200 text-muted-foreground hover:text-ink flex items-center gap-1.5 text-xs font-bold transition-colors"><ExternalLink className="h-4 w-4" /> Open</a>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Want a branded short link like <span className="font-mono">docs.mycommunity.com/join</span>? Set a{" "}
              <span className="font-semibold">custom short domain</span> in Agent Config → Documents. The same domain
              powers document short links AND this opt-in URL.
            </p>
          </>
        )}
      </div>

      {/* access requests */}
      <section className="bg-white rounded-2xl border border-sand-200 p-5" data-testid="access-requests-panel">
        <h2 className="font-heading font-bold text-ink flex items-center gap-2 mb-1"><UserPlus className="h-4.5 w-4.5 text-redline-500" /> Portal access requests {pending.length > 0 && <span className="rounded-full bg-redline-500 text-white text-xs font-bold px-2 py-0.5">{pending.length}</span>}</h2>
        <p className="text-xs text-muted-foreground mb-3">People who asked to join from the public page or portal login. Approving adds them to the roster (attached to their unit) and texts them a welcome.</p>
        {pending.length === 0 ? <p className="text-sm text-muted-foreground bg-sand-50 border border-sand-100 rounded-xl px-3.5 py-2.5">No pending requests.</p> : (
          <div className="space-y-2">
            {pending.map((r) => (
              <div key={r.id} data-testid={`access-req-${r.id}`} className="rounded-xl border border-sand-200 p-3.5 flex flex-wrap items-center gap-3">
                <div className="min-w-[12rem] flex-1">
                  <div className="font-semibold text-sm text-ink">{r.name}{homeLabel(r) && <span className="text-muted-foreground font-normal"> · {homeLabel(r)}</span>}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Phone className="h-3 w-3" /> {r.phone}{r.email ? ` · ${r.email}` : ""} · {fmtStamp(r.created_at)}</div>
                  {r.note && <div className="text-xs text-ink-600 italic mt-1">“{r.note}”</div>}
                </div>
                {!readOnly && (
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`approve-req-${r.id}`} disabled={busy === r.id + "approve"} onClick={() => decide(r, "approve")} className="rounded-full bg-emerald-600 text-white text-xs font-bold px-4 py-2 hover:bg-emerald-700 transition-colors flex items-center gap-1.5 disabled:opacity-60"><Check className="h-3.5 w-3.5" /> Approve</button>
                    <button data-testid={`deny-req-${r.id}`} disabled={busy === r.id + "deny"} onClick={() => decide(r, "deny")} className="rounded-full bg-sand-100 text-ink-700 text-xs font-bold px-4 py-2 hover:bg-sand-200 transition-colors flex items-center gap-1.5 disabled:opacity-60"><X className="h-3.5 w-3.5" /> Deny</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {decided.length > 0 && (
          <details className="mt-3">
            <summary className="text-xs font-bold text-muted-foreground cursor-pointer">Recently handled ({decided.length})</summary>
            <div className="mt-2 space-y-1">
              {decided.map((r) => (
                <div key={r.id} className="text-xs text-muted-foreground flex items-center gap-2 px-1">
                  <span className={`h-1.5 w-1.5 rounded-full ${r.status === "approved" ? "bg-emerald-500" : "bg-sand-400"}`} />
                  {r.name} · {r.phone} · {r.status} {r.decided_at ? fmtStamp(r.decided_at) : ""}
                </div>
              ))}
            </div>
          </details>
        )}
      </section>

      {/* content editor */}
      <CollapsibleCard id="public.site-editor" icon={FileText} title="Page content" testid="site-editor" mobileDefaultOpen={true}>
        <div className="mt-4 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Web address" hint="letters, numbers, dashes">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground shrink-0">/c/</span>
              <input data-testid="site-slug" disabled={readOnly} value={site.slug} onChange={(e) => upd("slug", e.target.value.toLowerCase())} className={inputCls} />
            </div>
          </Field>
          <Field label="Headline" hint={`one warm line under the ${V.lc("org")} name`}>
            <input data-testid="site-headline" disabled={readOnly} value={site.headline} onChange={(e) => upd("headline", e.target.value)} className={inputCls} />
          </Field>
        </div>
        <Field label="Welcome message">
          <textarea data-testid="site-welcome" disabled={readOnly} rows={3} value={site.welcome} onChange={(e) => upd("welcome", e.target.value)} className={`${inputCls} resize-y`} placeholder={`A short hello to ${V.lcs("person")} and visitors…`} />
        </Field>
        <Field label={`About the ${V.lc("org")}`} hint="optional">
          <textarea data-testid="site-about" disabled={readOnly} rows={3} value={site.about} onChange={(e) => upd("about", e.target.value)} className={`${inputCls} resize-y`} placeholder="History, vibe, what makes it special…" />
        </Field>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Contact name"><input data-testid="site-contact-name" disabled={readOnly} value={site.contact_name} onChange={(e) => upd("contact_name", e.target.value)} className={inputCls} placeholder="Management contact" /></Field>
          <Field label="Contact email"><input data-testid="site-contact-email" disabled={readOnly} value={site.contact_email} onChange={(e) => upd("contact_email", e.target.value)} className={inputCls} /></Field>
          <Field label="Contact phone"><input data-testid="site-contact-phone" disabled={readOnly} value={site.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} className={inputCls} /></Field>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2.5"><Switch data-testid="site-show-amenities" disabled={readOnly} checked={!!site.show_amenities} onCheckedChange={(v) => upd("show_amenities", v)} /><span className="text-sm font-semibold">Show amenities</span></div>
          <div className="flex items-center gap-2.5"><Switch data-testid="site-show-events" disabled={readOnly} checked={!!site.show_events} onCheckedChange={(v) => upd("show_events", v)} /><span className="text-sm font-semibold">Show upcoming events</span></div>
        </div>
        <div>
          <label className="text-sm font-semibold">Cover photo</label>
          <div className="mt-2 flex items-center gap-3">
            {site.photo_path ? (
              <span className="text-xs text-emerald-700 bg-emerald-50 rounded-full px-3 py-1.5 font-bold">Photo set ✓</span>
            ) : <span className="text-xs text-muted-foreground">No photo yet — the page uses a clean dark hero.</span>}
            {!readOnly && (
              <>
                <button data-testid="site-upload-photo" onClick={() => fileRef.current?.click()} className="rounded-full border border-sand-200 bg-white px-4 py-2 text-xs font-bold text-ink hover:border-ink transition-colors flex items-center gap-1.5"><ImagePlus className="h-4 w-4" /> {site.photo_path ? "Replace photo" : "Upload photo"}</button>
                {site.photo_path && <button data-testid="site-remove-photo" onClick={() => upd("photo_path", "")} className="text-xs font-bold text-muted-foreground hover:text-danger-600">Remove</button>}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
              </>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Concierge number shown on the page: <b>{conciergeNum || "none assigned yet"}</b> · The page always includes {V.lc("person")} sign-in, request-access, and the Ask Redline footer.</p>
        </div>
      </CollapsibleCard>

      {/* branding — the community's own look */}
      <CollapsibleCard id="public.site-branding-editor" icon={Palette} title="Branding" testid="site-branding-editor" defaultOpen={false}>
        <div className="mt-4 space-y-4">
        <p className="text-xs text-muted-foreground -mt-2">Make the page yours — your accent color drives buttons and highlights, and your logo tops the hero.</p>
        <div>
          <label className="text-sm font-semibold">Accent color</label>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {BRAND_PRESETS.map(([hex, name]) => {
              const active = (site.brand_color || "#D32F2F").toLowerCase() === hex.toLowerCase();
              return (
                <button key={hex} data-testid={`brand-swatch-${hex.slice(1)}`} disabled={readOnly} title={name}
                  onClick={() => upd("brand_color", hex === "#D32F2F" ? "" : hex)}
                  className={`h-9 w-9 rounded-full transition-transform ${active ? "ring-2 ring-offset-2 ring-ink scale-110" : "hover:scale-110"}`}
                  style={{ backgroundColor: hex }} />
              );
            })}
            <label className="ml-1 inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-3 py-1.5 text-xs font-bold text-ink/70 cursor-pointer hover:border-ink transition-colors">
              <input type="color" data-testid="brand-color-custom" disabled={readOnly} value={site.brand_color || "#D32F2F"}
                onChange={(e) => upd("brand_color", e.target.value)} className="h-5 w-7 cursor-pointer border-0 bg-transparent p-0" />
              Custom
            </label>
            {site.brand_color && (
              <button data-testid="brand-color-reset" disabled={readOnly} onClick={() => upd("brand_color", "")}
                className="text-xs font-bold text-muted-foreground hover:text-ink transition-colors">Reset to Redline red</button>
            )}
          </div>
          <div className="mt-3 inline-flex items-center gap-2 rounded-xl bg-sand-50 border border-sand-100 px-3 py-2">
            <span className="text-xs text-muted-foreground">Preview:</span>
            <span className="rounded-full text-white text-xs font-bold px-4 py-1.5" style={{ backgroundColor: site.brand_color || "#D32F2F" }} data-testid="brand-preview-pill">Text the concierge</span>
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold">{V.one("org")} logo</label>
          <div className="mt-2 flex items-center gap-3">
            {site.logo_path ? (
              <span className="text-xs text-emerald-700 bg-emerald-50 rounded-full px-3 py-1.5 font-bold" data-testid="brand-logo-set">Logo set ✓</span>
            ) : <span className="text-xs text-muted-foreground">No logo yet — the page shows your {V.lc("org")} name alone.</span>}
            {!readOnly && (
              <>
                <button data-testid="brand-upload-logo" onClick={() => logoRef.current?.click()} className="rounded-full border border-sand-200 bg-white px-4 py-2 text-xs font-bold text-ink hover:border-ink transition-colors flex items-center gap-1.5"><ImagePlus className="h-4 w-4" /> {site.logo_path ? "Replace logo" : "Upload logo"}</button>
                {site.logo_path && <button data-testid="brand-remove-logo" onClick={() => upd("logo_path", "")} className="text-xs font-bold text-muted-foreground hover:text-danger-600">Remove</button>}
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">PNG with a transparent background looks best on the dark hero.</p>
        </div>
        </div>
      </CollapsibleCard>

      {/* payments & dues */}
      <CollapsibleCard id="public.site-payments-editor" icon={CreditCard} title={`Payments & ${V.lcs("dues")}`} testid="site-payments-editor" defaultOpen={false} summary={site.show_payments ? "shown on page" : "hidden"}>
        <div className="mt-4 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Tell {V.lcs("person")} how to pay {V.lcs("dues")} today.</span>
          <div className="flex items-center gap-2.5"><span className="text-xs font-semibold text-muted-foreground">Show on page</span><Switch data-testid="site-show-payments" disabled={readOnly} checked={!!site.show_payments} onCheckedChange={(v) => upd("show_payments", v)} /></div>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">Mailing lockbox, online link, association ID. (Full in-app payments come later; this works right now.)</p>
        {site.show_payments && (
          <>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Assessment frequency"><input data-testid="site-pay-frequency" disabled={readOnly} value={site.pay_frequency} onChange={(e) => upd("pay_frequency", e.target.value)} className={inputCls} placeholder="Monthly" /></Field>
              <Field label="Association / account ID" hint={`${V.lcs("person")} reference this when paying`}><input data-testid="site-pay-assoc-id" disabled={readOnly} value={site.pay_association_id} onChange={(e) => upd("pay_association_id", e.target.value)} className={inputCls} placeholder="e.g. MAPLE01" /></Field>
              <Field label="Accounting contact" hint="phone or email">
                <input data-testid="site-pay-accounting" disabled={readOnly} value={site.pay_accounting} onChange={(e) => upd("pay_accounting", e.target.value)} className={inputCls} placeholder="(855) 555-0100 · billing@…" />
              </Field>
            </div>
            <Field label="Mail payments to" hint="payee name">
              <input data-testid="site-pay-mail-name" disabled={readOnly} value={site.pay_mail_name} onChange={(e) => upd("pay_mail_name", e.target.value)} className={inputCls} placeholder={V.isHoa ? `Maplewood ${V.one("org")} ${V.one("org_short")}, Inc.` : "Acme Roofing LLC"} />
            </Field>
            <Field label="Mailing / lockbox address">
              <textarea data-testid="site-pay-mail-address" disabled={readOnly} rows={3} value={site.pay_mail_address} onChange={(e) => upd("pay_mail_address", e.target.value)} className={`${inputCls} resize-y`} placeholder={"c/o Processing Center\nPO Box 12345\nAustin, TX 78701"} />
            </Field>
          </>
        )}
        </div>
      </CollapsibleCard>

      {/* quick links */}
      <CollapsibleCard id="public.site-links-editor" icon={Link2} title="Quick links" testid="site-links-editor" defaultOpen={false}>
        <div className="mt-4 space-y-3">
        <p className="text-xs text-muted-foreground -mt-1">Point {V.lcs("person")} anywhere — architectural request forms, insurance certificates, gate access devices, city services. Up to 10.</p>
        {(site.quick_links || []).map((l, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input data-testid={`site-link-label-${i}`} disabled={readOnly} value={l.label} placeholder="Label (e.g. Architectural Request Form)"
              onChange={(e) => upd("quick_links", site.quick_links.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className={inputCls} />
            <input data-testid={`site-link-url-${i}`} disabled={readOnly} value={l.url} placeholder="https://…"
              onChange={(e) => upd("quick_links", site.quick_links.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} className={inputCls} />
            {!readOnly && <button data-testid={`site-link-remove-${i}`} onClick={() => upd("quick_links", site.quick_links.filter((_, j) => j !== i))} className="h-9 w-9 shrink-0 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-danger-600 flex items-center justify-center transition-colors"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
        {!readOnly && (site.quick_links || []).length < 10 && (
          <button data-testid="site-link-add" onClick={() => upd("quick_links", [...(site.quick_links || []), { label: "", url: "" }])}
            className="rounded-full border border-sand-200 bg-white px-4 py-2 text-xs font-bold text-ink hover:border-ink transition-colors flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add link</button>
        )}
        </div>
      </CollapsibleCard>

      {/* public documents — what's live on the page vs. what you can add */}
      <CollapsibleCard id="public.site-docs-editor" icon={FileText} title="Public documents" testid="site-docs-editor" defaultOpen={false}>
        <div className="mt-4 space-y-4">
        <p className="text-xs text-muted-foreground -mt-1">These download from your page with no sign-in (CC&Rs, bylaws, forms). Everything else stays {V.lc("person")}-only. Tip: flag a whole folder as public under Documents to publish everything in it automatically.</p>
        {(() => {
          const selectedIds = site.public_doc_ids || [];
          const onPage = docs.filter((d) => d.via_folder || selectedIds.includes(d.id));
          const q = docSearch.trim().toLowerCase();
          const addable = docs.filter((d) => !d.via_folder && !selectedIds.includes(d.id) && (!q || (d.title || "").toLowerCase().includes(q)));
          return (
            <>
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1.5">On your public page ({onPage.length})</div>
                {onPage.length === 0 ? (
                  <p className="text-sm text-muted-foreground bg-sand-50 border border-sand-100 rounded-xl px-3.5 py-2.5">Nothing published yet — add documents below.</p>
                ) : (
                  <div className="space-y-1.5">
                    {onPage.map((d) => (
                      <div key={d.id} data-testid={`site-pubdoc-${d.id}`} className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50/40 px-3.5 py-2.5 text-sm">
                        <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                        <span className="font-medium text-ink truncate flex-1">{d.title}</span>
                        {d.via_folder ? (
                          <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2.5 py-1" title="Published automatically — manage in Documents">
                            <FolderOpen className="h-3 w-3" /> Public folder · {d.via_folder}
                          </span>
                        ) : !readOnly && (
                          <button data-testid={`site-pubdoc-remove-${d.id}`} title="Remove from the public page"
                            onClick={() => upd("public_doc_ids", selectedIds.filter((x) => x !== d.id))}
                            className="shrink-0 h-7 w-7 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-redline-600 flex items-center justify-center transition-colors">
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!readOnly && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1.5">Add a document</div>
                  {docs.length === 0 ? (
                    <p className="text-sm text-muted-foreground bg-sand-50 border border-sand-100 rounded-xl px-3.5 py-2.5">No documents uploaded yet — add some under Documents first.</p>
                  ) : (
                    <>
                      <div className="relative max-w-sm mb-2">
                        <Search className="h-4 w-4 text-ink/40 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input data-testid="site-doc-search" value={docSearch} onChange={(e) => setDocSearch(e.target.value)} placeholder="Search your library…"
                          className="w-full rounded-full border border-sand-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-redline-300" />
                      </div>
                      {addable.length === 0 ? (
                        <p className="text-xs text-muted-foreground px-1">{q ? "No matches — try another search." : "Everything in your library is already on the page."}</p>
                      ) : (
                        <div className="grid sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1">
                          {addable.map((d) => (
                            <button key={d.id} data-testid={`site-doc-add-${d.id}`}
                              onClick={() => upd("public_doc_ids", [...selectedIds, d.id])}
                              className="flex items-center gap-2.5 rounded-xl border border-sand-200 bg-white px-3.5 py-2.5 text-sm text-left hover:border-ink transition-colors group">
                              <Plus className="h-4 w-4 text-ink/40 group-hover:text-redline-500 shrink-0 transition-colors" />
                              <span className="font-medium text-ink truncate">{d.title}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          );
        })()}
        </div>
      </CollapsibleCard>

      {/* management company */}
      <CollapsibleCard id="public.site-mgmt-editor" icon={Building2} title="Management company" testid="site-mgmt-editor" defaultOpen={false}>
        <div className="mt-4 space-y-4">
        <p className="text-xs text-muted-foreground -mt-2">Shown in the page footer. Pre-filled from your management company where available.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Company name"><input data-testid="site-mgmt-name" disabled={readOnly} value={site.mgmt_name} onChange={(e) => upd("mgmt_name", e.target.value)} className={inputCls} /></Field>
          <Field label="Company website" hint="optional"><input data-testid="site-mgmt-website" disabled={readOnly} value={site.mgmt_website} onChange={(e) => upd("mgmt_website", e.target.value)} className={inputCls} placeholder="https://…" /></Field>
        </div>
        </div>
      </CollapsibleCard>
      {!readOnly && <StickySaveBar dirty={dirty} saving={saving} onSave={() => save()} label="Public page changed" testid="site-sticky" />}
      {dirty && <div className="h-20" aria-hidden="true" />}
    </div>
  );
}
